// NOTE: All requests to remote servers must pass through these functions.
import { dynamicImports } from "./DynamicImports";
import * as pluginsApi from "@/Api/Plugins";
import { appName, delayForPopupOpenClose, isLocalHost, isTest, isTour } from "./GlobalVars";
import { localStorageSetItem } from "./LocalStorage";
import { getSetting } from "@/Plugins/Core/Settings/LoadSaveSettings";
import { messagesApi } from "@/Api/Messages";
import { detectPlatform, HostOs } from "./HostOs";
import { PopupVariant } from "@/UI/MessageAlerts/Popups/InterfacesAndEnums";
export var ResponseType;
(function (ResponseType) {
    ResponseType["JSON"] = "json";
    ResponseType["TEXT"] = "text";
    ResponseType["BLOB"] = "blob";
    ResponseType["ARRAY_BUFFER"] = "arraybuffer";
})(ResponseType || (ResponseType = {}));
/**
 * Introduce a random delay. Only used for testing on localhost.
 *
 * @returns {Promise<void>} A promise that resolves after the delay.
 */
async function _introduceRandomDelayForTesting() {
    // Add a random delay between 0 and 2 seconds (2000ms)
    const delay = Math.random() * 2000;
    console.log(`Fetcher (localhost testing): introducing delay of ${delay.toFixed(0)}ms`);
    return new Promise((resolve) => setTimeout(resolve, delay));
}
const failingUrlSubstrings = new Set();
/**
 * Adds a URL substring to the set of URLs that should fail during tests.
 *
 * @param {string} substring The substring to match in URLs.
 */
export function addFailingUrlSubstring(substring) {
    if (isTest) {
        failingUrlSubstrings.add(substring);
    }
}
/**
 * Fetch a file from a remote URL.
 *
 * @param {string} url  The URL of the file to fetch.
 * @param {IFetcherOptions} [options]  The options for the fetch.
 * @returns {Promise<any>} A promise that resolves the fetched file.
 */
export async function fetcher(url, options) {
    if (isTest && Array.from(failingUrlSubstrings).some(substring => url.includes(substring))) {
        const msg = `Fetcher (testing): Simulating failure for URL: ${url}`;
        console.warn(msg);
        throw new Error("Simulated 404 Not Found");
    }
    // If options not specified, figure out what it should be based on the URL.
    if (options === undefined) {
        // if url ends in json, set responseType to JSON
        if (url.toLowerCase().endsWith(".json")) {
            options = {
                responseType: ResponseType.JSON,
            };
        }
        else {
            options = {
                responseType: ResponseType.TEXT,
            };
        }
    }
    // Determine if it is an external URL. External URLS have http:// or
    // https://, and are not local.
    const urlUpper = url.toUpperCase();
    const startsWithHttp = urlUpper.slice(0, 7) === "HTTP://" ||
        urlUpper.slice(0, 8) === "HTTPS://";
    const isExternal = startsWithHttp && !url.startsWith(window.location.origin) && url.indexOf(appName.toLowerCase() + ".") === -1;
    const allowExternalWebAccess = (await getSetting("allowExternalWebAccess"));
    let permissionResp = "allowed"; // for internal urls (!isExternal).
    if (isExternal && !isTest && !isTour) {
        if (allowExternalWebAccess) {
            // User has already allowed access. Show a toast.
            const settingsPath = detectPlatform() === HostOs.Mac
                ? `${appName} → Settings...`
                : "Edit → Preferences...";
            const baseUrl = new URL(url).hostname;
            messagesApi.popupMessage("Accessing External Resource", `Coordinating with <b>${baseUrl}</b> because you have allowed access to all external resources. You can manage this permission via <b>${settingsPath}</b>.`, PopupVariant.Info, undefined, false, {});
        }
        else {
            // User has not allowed access. Ask for permission.
            permissionResp = await new Promise((resolve) => {
                // Stop the spinner
                pluginsApi.pluginsApi.runPlugin("fetcherpermission", {
                    url,
                    /**
                     * Called when the user denies permission.
                     */
                    onDeny: () => {
                        setTimeout(() => {
                            resolve("denied");
                        }, delayForPopupOpenClose);
                    },
                    /**
                     * Called when the user allows permission.
                     */
                    onAllow: () => {
                        setTimeout(() => {
                            resolve("allowed");
                        }, delayForPopupOpenClose);
                    },
                    /**
                     * Called when the user allows all external access.
                     */
                    onAllowAll: async () => {
                        // TODO: Update settings for allow all
                        localStorageSetItem("allowExternalWebAccess", true);
                        setTimeout(() => {
                            resolve("allowed");
                        }, delayForPopupOpenClose);
                    },
                });
            });
        }
    }
    if (permissionResp === "denied") {
        throw new Error("Permission denied; cannot fetch the URL.");
    }
    // If cacheBust is true, add a cache-busting query parameter to the URL.
    if (options.cacheBust) {
        const cacheBustParam = `cacheBust=${Date.now()}`;
        url = url.includes("?")
            ? `${url}&${cacheBustParam}`
            : `${url}?${cacheBustParam}`;
    }
    // *** START localhost delay modification ***
    // If running on localhost and fetching an external resource, introduce a random delay for testing race conditions.
    if (isLocalHost && !isTest) {
        await _introduceRandomDelayForTesting();
    }
    // *** END localhost delay modification ***
    // Load axios
    const axios = await dynamicImports.axios.module;
    if (!options.formPostData) {
        // Fetch the file from the remote URL using axios, simple GET.
        const response = await axios.get(url, options);
        return response.data;
    }
    else {
        // Fetch the file from the remote URL using axios, POST.
        const response = await axios.post(url, options.formPostData, options);
        return response.data;
    }
}
/**
 * A queue that handles rate-limited HTTP requests using the fetcher function.
 * Ensures that requests are made at a controlled rate to prevent overwhelming
 * target servers or hitting rate limits.
 */
export class RateLimitedFetcherQueue {
    queue = [];
    completedTimes = []; // Track when requests complete
    requestsPerSecond;
    options;
    isProcessing = false;
    /**
     * Creates a new RateLimitedFetcherQueue instance.
     *
     * @param {number}       requestsPerSecond     Maximum number of requests
     *                                             allowed per second
     * @param {object}       options               Configuration options for the
     *                                             fetcher
     * @param {ResponseType} options.responseType  Type of response expected
     *                                             (json, text, blob, etc.)
     * @throws {Error} If requestsPerSecond is less than or equal to 0
     */
    constructor(requestsPerSecond, options) {
        if (requestsPerSecond <= 0) {
            throw new Error("requestsPerSecond must be greater than 0");
        }
        this.requestsPerSecond = requestsPerSecond;
        this.options = options;
    }
    /**
     * Adds a URL to the fetch queue. The request will be processed according to
     * the rate limits configured for the queue.
     *
     * @param {string}                url             The URL to fetch.
     * @param {IQueueRequestOptions} [requestOptions]  Optional per-request
     *     overrides. Supply formPostData to issue a POST, and/or responseType
     *     to override the queue default for this one request.
     * @returns {Promise<any>} A promise that resolves with the fetch response
     *                        or rejects if the fetch fails
     */
    enqueue(url, requestOptions) {
        return new Promise((resolve, reject) => {
            this.queue.push({ url, resolve, reject, requestOptions });
            this.processQueue();
        });
    }
    /**
     * Processes the queue while respecting rate limits. This method is called
     * automatically when new items are added to the queue.
     *
     * @private
     * @returns {Promise<void>}
     */
    async processQueue() {
        if (this.isProcessing) {
            return;
        }
        this.isProcessing = true;
        try {
            while (this.queue.length > 0) {
                if (this.canMakeNewRequest()) {
                    const item = this.queue.shift();
                    if (!item) {
                        break;
                    }
                    // Process the request and track its completion time
                    await this.processFetchRequest(item);
                    this.completedTimes.push(Date.now());
                    // Keep only the last second's worth of completed requests
                    const oneSecondAgo = Date.now() - 1000;
                    this.completedTimes = this.completedTimes.filter((time) => time > oneSecondAgo);
                }
                else {
                    // Wait until we can make another request
                    const oldestRequest = this.completedTimes[0];
                    const waitTime = Math.max(0, 1000 - (Date.now() - oldestRequest));
                    await new Promise((resolve) => setTimeout(resolve, waitTime));
                }
            }
        }
        finally {
            this.isProcessing = false;
        }
    }
    /**
     * Processes a single fetch request and handles its resolution or rejection.
     *
     * @private
     * @param {QueueItem} item - The queue item containing the URL and promise handlers
     * @returns {Promise<void>}
     */
    async processFetchRequest(item) {
        try {
            // Merge the queue defaults with any per-request overrides so a
            // single enqueue can flip to POST/CSV (with the right headers)
            // while leaving every other call on the GET/JSON default.
            const fetchOptions = {
                responseType: item.requestOptions?.responseType ??
                    this.options.responseType,
                formPostData: item.requestOptions?.formPostData,
                headers: item.requestOptions?.headers,
            };
            const response = await fetcher(item.url, fetchOptions);
            item.resolve(response);
        }
        catch (error) {
            item.reject(error);
        }
    }
    /**
     * Checks if a new request can be made based on the configured rate limit.
     * Takes into account the number of requests completed in the last second.
     *
     * @private
     * @returns {boolean} True if a new request can be made, false otherwise
     */
    canMakeNewRequest() {
        const now = Date.now();
        const oneSecondAgo = now - 1000;
        // Clean up old completion times
        this.completedTimes = this.completedTimes.filter((time) => time > oneSecondAgo);
        // Check if we're under the rate limit
        return this.completedTimes.length < this.requestsPerSecond;
    }
    /**
     * Gets the current number of items waiting in the queue.
     *
     * @returns {number} The number of queued items
     */
    get length() {
        return this.queue.length;
    }
    /**
     * Clears all pending requests from the queue and resets the completion times.
     * Active requests will still complete but no new requests will be processed
     * until new items are enqueued.
     */
    clear() {
        this.queue = [];
        this.completedTimes = [];
    }
}
